// customizations:
// - 157 and 165: changed index for category filter
// - fixed filters from qs and block: the block ones override the qs ones
// - re-exported getLocalizedVocabulary from the base theme so the base
//   Body.jsx (which imports it from this module) keeps working even though
//   this customization otherwise fully replaces the base searchEventsUtils.js

import moment from 'moment';
import intersection from 'lodash/intersection';
import { getVocabulary } from '@plone/volto/actions/vocabularies/vocabularies';

// Volto's getVocabulary always requests /@vocabularies/... at the site root,
// which carries no language segment; the response is then cached (browser +
// Varnish) by URL and shared across languages. Prefix the request with the
// current language root (/en, /it, ...) so each language has its own URL/cache
// key and gets the correctly translated terms. The result still lands in
// state.vocabularies[vocabNameOrURL] because the reducer keys on that name.
export function getLocalizedVocabulary({ locale, ...rest }) {
  const action = getVocabulary(rest);
  if (locale) {
    action.request.path = `/${locale}${action.request.path}`;
  }
  return action;
}

export const DEFAULT_B_SIZE = 9;
export const FILTERS_ACTIONS = {
  DATE: 'DATE',
  TEXT: 'TEXT',
  SUBJECT: 'SUBJECT',
  CITY: 'CITY',
  RESET: 'RESET',
};

export const datesForDisplay = (start, end) => {
  const mStart = moment(start);
  const mEnd = moment(end);
  if (!mStart.isValid() || !mEnd.isValid()) {
    return null;
  }
  const sameYear = mStart.isSame(mEnd, 'year');
  const sameMonth = mStart.isSame(mEnd, 'month');
  const sameDay = mStart.isSame(mEnd, 'day');
  const sameTime = mStart.isSame(mEnd, 'minute');

  return {
    sameYear,
    sameMonth,
    sameDay,
    sameTime,
    mStart,
    mEnd,
  };
};

export const getDefaultStartEndDate = (whichEvents) => {
  let d = { startDate: null, endDate: null };
  switch (whichEvents) {
    case 'none':
      d.startDate = null;
      d.endDate = null;
      break;
    case 'next':
      d.startDate = moment().startOf('day');
      d.endDate = null;
      break;
    case 'today':
      d.startDate = moment().startOf('day');
      d.endDate = moment().endOf('day');
      break;
    case 'weekend':
      const friday = 5;
      const sunday = 7;
      const now_weekday = moment().isoWeekday();
      const nextFriday =
        now_weekday < friday
          ? moment().add(friday - now_weekday, 'days')
          : moment();
      const nextSunday =
        now_weekday < sunday
          ? moment().add(sunday - now_weekday, 'days')
          : moment();
      d.startDate = nextFriday.startOf('day');
      d.endDate = nextSunday.endOf('day');
      break;
    case 'month':
      d.startDate = moment().startOf('month');
      d.endDate = moment().endOf('month');
      break;
    default:
      break;
  }

  return d;
};

export const getFiltersReducer =
  (initialFiltersState) =>
  (state = initialFiltersState, action) => {
    let newState = { ...state };

    switch (action.type) {
      case FILTERS_ACTIONS.TEXT:
        newState.text = action.value;
        newState.changedFilters = true;
        break;

      case FILTERS_ACTIONS.SUBJECT:
        newState.subject = action.value;
        newState.changedFilters = true;
        break;
      case FILTERS_ACTIONS.CITY:
        newState.city = action.value;
        newState.changedFilters = true;
        break;

      case FILTERS_ACTIONS.DATE:
        newState.date = {
          startDate: action.value.startDate ?? state.startDate,
          endDate: action.value.endDate ?? state.endDate,
        };
        newState.changedFilters = true;
        break;

      case FILTERS_ACTIONS.RESET:
        newState = {
          ...initialFiltersState,
        };

        break;

      default:
        return newState;
    }

    return newState;
  };
export const getQueryFilter = (query, i, o) => {
  let ret = null;
  query.forEach((f) => {
    if (i && f.i === i && o && o === f.o) {
      ret = f;
    }
  });
  return ret;
};
export const getFilters = (defaultQuery = [], filterValues) => {
  const { text, subject, date, city } = filterValues;
  let filters = JSON.parse(JSON.stringify(defaultQuery));

  if (
    !getQueryFilter(
      filters,
      'portal_type',
      'plone.app.querystring.operation.selection.any',
    )
  ) {
    filters.push({
      i: 'portal_type',
      o: 'plone.app.querystring.operation.selection.any',
      v: ['Event'],
    });
  }

  if (text && text.length) {
    filters.push({
      i: 'SearchableText',
      o: 'plone.app.querystring.operation.string.contains',
      v: text + '*',
    });
  }

  if (subject) {
    let defaultSubjectCondition = getQueryFilter(
      filters,
      'event_category',
      'plone.app.querystring.operation.selection.any',
    );

    if (defaultSubjectCondition) {
      defaultSubjectCondition.v = intersection(defaultSubjectCondition.v, [
        subject,
      ]);
    } else {
      filters.push({
        i: 'event_category',
        o: 'plone.app.querystring.operation.selection.any',
        v: subject,
      });
    }
  }

  if (date.startDate) {
    filters.push({
      i: 'start',
      o: 'plone.app.querystring.operation.date.largerThan',
      v: date.startDate.clone().startOf('day')?.utc().toISOString(),
    });
  }

  if (date.endDate) {
    filters.push({
      // changed for v2.0.0-alpha.15
      // it makes no sense to search by end date
      // plone calculates the occurrence of an event depending on the start date
      // searching for an event from a date x to a date y only makes sense if checking the start date
      // using index "end" creates issues
      // es. recurrent event from 1st to 28th of february excluding 4th of february
      // if "end" is used and search is event start === larger than 4th and end === less than 4th
      // the event will be returned as it fits the filters but it does not actually happen on that day
      i: 'start',
      o: 'plone.app.querystring.operation.date.lessThan',
      v: date.endDate.clone().endOf('day')?.utc().toISOString(),
    });
  }

  if (city) {
    let defaultCityCondition = getQueryFilter(
      filters,
      'city',
      'plone.app.querystring.operation.selection.any',
    );

    if (defaultCityCondition) {
      defaultCityCondition.v = intersection(defaultCityCondition.v, [city]);
    } else {
      filters.push({
        i: 'city',
        o: 'plone.app.querystring.operation.selection.any',
        v: city,
      });
    }
  }

  return filters;
};
